import { Cents } from '@systemeio/types'
import { centsToUnits } from '@systemeio/util'
import {
  UNLIMITED_CRM_PIPELINE,
  UNLIMITED_TRESHOLD,
  UNLIMITED_VARIANTS_TRESHOLD,
} from 'modules/profile/user-plan/constants/user-plan-consts'
import {
  BillingPeriodEnum,
  SubscriptionPeriodEnum,
} from 'modules/profile/user-plan/enums/billing-period-enum'
import {
  UserPlanColumnsInterface,
  UserPlanInterface,
  UseUserPlanInterface,
} from 'modules/profile/user-plan/types/user-plan-interface'
import { TLocoType } from 'shared/hooks/use-loco-translation'

export const getFeatureLimitTitle = ({
  pricePlan,
  t,
  key,
}: {
  pricePlan: UserPlanInterface
  t: TLocoType
  key: UserPlanColumnsInterface['key']
}) => {
  const pricePlanFeatureValue = pricePlan.limits[key]

  if (pricePlanFeatureValue === null) {
    return t('global.unlimited')
  }

  switch (key) {
    case 'maxProductVariants': {
      if (pricePlanFeatureValue >= UNLIMITED_VARIANTS_TRESHOLD) {
        return t('global.unlimited')
      }
      break
    }
    case 'maxCrmPipelines': {
      if (pricePlanFeatureValue >= UNLIMITED_CRM_PIPELINE) {
        return t('global.unlimited')
      }
      break
    }
    case 'maxTotalRevenue': {
      return `${centsToUnits(pricePlanFeatureValue as Cents)} USD`
    }
    default: {
      if (pricePlanFeatureValue >= UNLIMITED_TRESHOLD) {
        return t('global.unlimited')
      }
    }
  }

  return pricePlanFeatureValue
}

export const isPricePlanFeatureExceeded = (
  userResources: UseUserPlanInterface['userResources'],
  pricePlan: UserPlanInterface,
  column: UserPlanColumnsInterface,
) => {
  const pricePlanFeatureValue = pricePlan.limits[column.key]

  if (!column.resourceKey) {
    return false
  }

  if (pricePlanFeatureValue === null) {
    return false
  }

  const userResourceValue = userResources[column.resourceKey]

  if (userResourceValue === null) {
    return false
  }

  return userResourceValue > pricePlanFeatureValue
}

export function getBillingPeriodFromSubscriptionPeriod(
  pricePlanInterval: SubscriptionPeriodEnum | null,
) {
  if (!pricePlanInterval) {
    return
  }

  return pricePlanInterval === SubscriptionPeriodEnum.month
    ? BillingPeriodEnum.monthly
    : BillingPeriodEnum.annual
}

export const isUserPlanActive = (
  pricePlan: UserPlanInterface,
  billingPeriod: BillingPeriodEnum,
) => {
  if (!pricePlan.currentUserPlan) {
    return false
  }

  if (!pricePlan.price) {
    return true
  }

  return getBillingPeriodFromSubscriptionPeriod(pricePlan.price.interval) === billingPeriod
}

export const isFeatureExceeded = (data: UseUserPlanInterface, column: UserPlanColumnsInterface) => {
  const activePricePlan = data.userPlans.find(pricePlan => pricePlan.currentUserPlan)

  if (activePricePlan) {
    return isPricePlanFeatureExceeded(data.userResources, activePricePlan, column)
  }

  return false
}
